import * as React from 'react';
import { TopHeadBandContext, TOP_HEADBAND_HEIGHT } from '../../containers_v2/topHeadBand/TopHeadBandProvider';
import { useKeyPress } from '../../utils/customHooks';
import { PopupMode, PopupProps, PopupStyle } from './model/Model';
import { PopupStateContext } from './PopupStateContext';
import { Background, Container, PopupWrapper } from './style/Style';
import { SidelyBlack } from '../../styles/global/css/Utils';
import { SIDEBAR_CLOSE_WIDTH } from '../sidebar/style';

function checkClickOut(ref: React.RefObject<HTMLDivElement | undefined>, clickOut: (() => void) | undefined, disableClickOut: boolean, setPopupState: React.Dispatch<React.SetStateAction<string[]>>, popupId: string) {
	const headBand = React.useContext(TopHeadBandContext);
	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			let target: HTMLElement | null = event.target as HTMLElement;
			while (target) {
				if (target?.id === 'global-loader') return;
				target = target?.parentElement;
			}
			if (!disableClickOut) {
				if (ref.current && !ref.current.contains(event.target as HTMLElement) && ((headBand.ref.current == null) || !headBand.ref.current?.contains(event.target as HTMLElement))) {
					if (clickOut) {
						setPopupState(state => {
							if (state[state.length - 1] === popupId) {
								clickOut();
							}
							return state;
						});
					}
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref, disableClickOut, headBand.ref, clickOut]);
}

export function subscribeToPopupState(isOpen: boolean, onClickOut: (() => void) | undefined, clickOutDisable: boolean, escDisabled: boolean, popupRef: React.RefObject<HTMLDivElement | undefined>): number {
	const { popupState, setPopupState } = React.useContext(PopupStateContext);
	const popupId = React.useId();
	const index = popupState.findIndex(p => p == popupId);

	React.useEffect(() => {
		const registered = popupState.includes(popupId);
		if (isOpen && !registered) setPopupState(state => [...state, popupId]);
		if (!(isOpen) && registered) setPopupState(state => state.filter(id => id !== popupId));
		return () => {
			if (popupState.includes(popupId)) setPopupState(state => state.filter(id => id !== popupId));
		};
	}, [isOpen]);

	// is execute before event js event onClick
	useKeyPress([{ key: 'Escape' }], () => {
		if (!escDisabled) {
			if (popupState[popupState.length - 1] == popupId) {
				onClickOut?.();
			}
		}
	});
	checkClickOut(popupRef, onClickOut, clickOutDisable, setPopupState, popupId);
	return index;

}

function Popup(props: PopupProps & { name?: string }): JSX.Element {
	const [clickOutDisable, setClickOutDisable] = React.useState<boolean>(props.disableOutClick ?? false);
	const [escDisabled, setEscDisabled] = React.useState<boolean>(props.disableEsc ?? false);
	const headBand = React.useContext(TopHeadBandContext);

	const wrapperRef = React.useRef<HTMLDivElement>(null);

	const [popupStyle, setPopupStyle] = React.useState<PopupStyle>(props.popupStyle ?? {});
	const [hasBackground, setHasBackground] = React.useState<boolean | undefined>(props.hasBackground);
	const [isOpen, setOpen] = React.useState<boolean>(false);

	const [animationEnd, setAnimationEnd] = React.useState<boolean>(false);
	const { popupState } = React.useContext(PopupStateContext);

	const index = subscribeToPopupState(props.isOpen, props.onClickOut, clickOutDisable, escDisabled, wrapperRef);

	React.useEffect(() => {
		const height = headBand.isOpen ? `calc(100% - ${SIDEBAR_CLOSE_WIDTH}px)` : '100%';
		const newPopupStyle = { ...(props.popupStyle ?? {}) };
		switch (props.popupMode) {
			case PopupMode.Centered:
				newPopupStyle.top = '50%';
				newPopupStyle.left = '50%';
				newPopupStyle.transform = 'translate(-50%, -50%)';
				newPopupStyle.position = 'fixed';
				newPopupStyle.borderRadius = '10px';
				if (popupStyle.fitContent)
					newPopupStyle.containerTransform = 'translateY(-50%)';
				setHasBackground(true);
				break;
			case PopupMode.Extended:
				newPopupStyle.width = `calc((100vw - ${SIDEBAR_CLOSE_WIDTH}px) * 0.75)`;
				newPopupStyle.left = `calc((100vw - ${SIDEBAR_CLOSE_WIDTH}px) * 0.25 + ${SIDEBAR_CLOSE_WIDTH}px)`;
				newPopupStyle.boxShadow = `0px 0px 16px color-mix(in oklab, ${SidelyBlack} 6%, transparent)`;
				newPopupStyle.animate = !isOpen;
				setHasBackground(false);
				break;
			case PopupMode.Default:
				newPopupStyle.width = `calc(100% - ${SIDEBAR_CLOSE_WIDTH}px)`;
				newPopupStyle.left = `${SIDEBAR_CLOSE_WIDTH}px`;
				newPopupStyle.boxShadow = undefined;
				newPopupStyle.animate = !isOpen;
				newPopupStyle.height = newPopupStyle.height ?? height;
				newPopupStyle.top = headBand.isOpen ? TOP_HEADBAND_HEIGHT : newPopupStyle.top ?? '0%';
				setHasBackground(true);
				break;
			case PopupMode.Details:
				newPopupStyle.width = '25vw';
				if (newPopupStyle.minWidth) {
					newPopupStyle.left = `min(100vw - ${newPopupStyle.minWidth}, 75vw)`;
				} else {
					newPopupStyle.left = '75vw';
				}
				newPopupStyle.boxShadow = `0px 0px 16px color-mix(in oklab, ${SidelyBlack} 6%, transparent)`;
				newPopupStyle.animate = !isOpen;
				newPopupStyle.height = newPopupStyle.height ?? height;
				newPopupStyle.top = headBand.isOpen ? TOP_HEADBAND_HEIGHT : newPopupStyle.top ?? '0%';
				setHasBackground(hasBackground ? true : false);
				break;
			default:
				break;
		}
		setPopupStyle(newPopupStyle);
	}, [JSON.stringify(props.popupStyle), props.popupMode, headBand.isOpen]);

	React.useEffect(() => {
		setClickOutDisable(props.disableOutClick ?? false);
	}, [props.disableOutClick]);

	React.useEffect(() => {
		setEscDisabled(props.disableEsc ?? false);
	}, [props.disableEsc]);

	React.useEffect(() => {
		if (!props.isOpen) {
			setAnimationEnd(false);
			setOpen(false);
		}
	}, [props.isOpen]);

	return (
		<div>
			{props.isOpen && (hasBackground || props.catchOutsideEventClick) && <Background zIndex={popupStyle.zIndex} noColor={props.catchOutsideEventClick} />}
			<PopupWrapper
				onAnimationEnd={() => setAnimationEnd(true)}
				animationStyle={popupStyle?.animationStyle}
				width={popupStyle?.width}
				height={popupStyle?.height ?? (popupStyle?.fitContent ? 'fit-content' : undefined)}
				top={popupStyle?.top}
				boxShadow={popupStyle?.boxShadow}
				borderRadius={popupStyle?.borderRadius}
				zIndex={popupStyle.zIndex}
				left={popupStyle.left}
				minWidth={popupStyle.minWidth}
				minHeight={popupStyle.minHeight}
				isOpen={props.isOpen}
				animate={popupStyle.animate && !animationEnd}
				transform={popupStyle.transform}
				position={popupStyle.position}
				maxHeight={popupStyle.maxHeight}
				noTransition={popupStyle.noTransition}
			>
				{props.isOpen && (
					<div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
						<Container
							borderRadius={popupStyle?.borderRadius}
							displayB={props.isOpen ? 'initial' : 'none'}
							padding={popupStyle.padding}
							overflow={popupStyle.overflow}
							fitContent={popupStyle?.fitContent}
							transform={popupStyle?.containerTransform}
						>
							<PopupProvider isChild={index > 0 || (index == -1 && popupState.length > 0)}>
								{props.content ?? props.children}
							</PopupProvider>
						</Container>
					</div>
				)}
			</PopupWrapper >
		</div>
	);
}

export default Popup;

interface PopupContextType {
	isChild: boolean
}

const defaultBehaviour: PopupContextType = {
	isChild: false
};

export const PopupContext = React.createContext<PopupContextType>(defaultBehaviour);

function PopupProvider(props: {
	children: React.ReactNode,
	isChild: boolean
}) {
	const { isChild } = props;

	return <PopupContext.Provider value={{ isChild }}>
		{props.children}
	</PopupContext.Provider>;
}
